import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { KeyRound } from 'lucide-react';
import { listPspCredentials, savePspCredentials } from '../../services/yoolpay/pspCredentialsApi';

/**
 * Registro de credenciales del PSP de la compañía (pago propio). ADR-015.
 * Las claves se envían a payment, que las CIFRA (AES-256-GCM) y las guarda; nunca
 * se guardan en claro ni pasan por la DB de tadmin. La lista muestra solo la
 * api_key enmascarada — el secret jamás vuelve.
 *
 * Requiere que la compañía ya exista (companiaId). Se registra un juego por
 * ambiente: `sandbox` (dev/QA) y `produccion`.
 */
const EMPTY = { ambiente: 'sandbox', apiKey: '', secretKey: '', apiBase: '', merchantId: '', planId: '' };

const PspCredentialsSection = ({ companiaId, proveedor }) => {
  const { getToken } = useAuth();
  const [form, setForm] = useState(EMPTY);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [ok, setOk] = useState(null);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      setRows(await listPspCredentials(token, companiaId));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [getToken, companiaId]);

  useEffect(() => {
    if (companiaId) load();
  }, [companiaId, load]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setOk(null);
    try {
      if (!form.apiKey || !form.secretKey) {
        throw new Error('api_key y secret_key son obligatorios');
      }
      const token = await getToken();
      await savePspCredentials(token, {
        companiaId,
        proveedor,
        ambiente: form.ambiente,
        apiKey: form.apiKey,
        secretKey: form.secretKey,
        ...(form.apiBase ? { apiBase: form.apiBase } : {}),
        ...(form.merchantId ? { merchantId: form.merchantId } : {}),
        ...(form.planId ? { config: { plan_id: form.planId } } : {}),
      });
      setOk(`Credenciales de ${proveedor} (${form.ambiente}) guardadas y cifradas.`);
      setForm({ ...EMPTY, ambiente: form.ambiente });
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <KeyRound className="h-5 w-5" />
          <span>Credenciales del PSP ({proveedor})</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Se envían cifradas a Yool Pay (AES-256-GCM). No se guardan en claro ni en
          esta base. Registra un juego por ambiente: <strong>sandbox</strong> para
          dev/QA y <strong>produccion</strong> para producción.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label htmlFor="psp_ambiente">Ambiente</Label>
            <Select value={form.ambiente} onValueChange={(v) => set('ambiente', v)}>
              <SelectTrigger id="psp_ambiente"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="sandbox">sandbox (dev/QA)</SelectItem>
                <SelectItem value="produccion">produccion</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="psp_api_base">API base (opcional)</Label>
            <Input id="psp_api_base" value={form.apiBase}
              placeholder="https://api.virtualpos-sandbox.com/v3"
              onChange={(e) => set('apiBase', e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="psp_api_key">API key</Label>
            <Input id="psp_api_key" value={form.apiKey} autoComplete="off"
              onChange={(e) => set('apiKey', e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="psp_secret_key">Secret key</Label>
            <Input id="psp_secret_key" type="password" value={form.secretKey} autoComplete="off"
              onChange={(e) => set('secretKey', e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="psp_merchant">Merchant ID (opcional)</Label>
            <Input id="psp_merchant" value={form.merchantId}
              onChange={(e) => set('merchantId', e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="psp_plan_id">Plan ID VirtualPOS (opcional)</Label>
            <Input id="psp_plan_id" value={form.planId}
              onChange={(e) => set('planId', e.target.value)} />
            <p className="text-xs text-muted-foreground">
              Plan VirtualPOS (PROGRAMA_DE_PAGOS) a reutilizar para las
              suscripciones de pago propio de esta compañía.
            </p>
          </div>
        </div>

        {error && <p className="text-sm text-red-600">{error}</p>}
        {ok && <p className="text-sm text-green-600">{ok}</p>}

        <Button type="button" onClick={handleSave} disabled={saving}>
          {saving ? 'Guardando…' : 'Guardar credenciales'}
        </Button>

        <div className="pt-2">
          <Label className="text-xs uppercase text-muted-foreground">Registradas</Label>
          {loading ? (
            <p className="text-sm text-muted-foreground">Cargando…</p>
          ) : rows.length === 0 ? (
            <p className="text-sm text-muted-foreground">Sin credenciales registradas.</p>
          ) : (
            <ul className="space-y-1 mt-2">
              {rows.map((r) => (
                <li key={r.id} className="flex items-center gap-2 text-sm">
                  <Badge variant={r.activo ? 'default' : 'secondary'}>{r.ambiente}</Badge>
                  <span className="font-mono">{r.proveedor}</span>
                  <span className="text-muted-foreground">key {r.apiKeyMasked}</span>
                  {r.config?.plan_id && <span className="text-muted-foreground">plan {r.config.plan_id}</span>}
                  {!r.activo && <span className="text-xs text-muted-foreground">(inactiva)</span>}
                </li>
              ))}
            </ul>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default PspCredentialsSection;
